/**
 * Context para gestionar el estado del banner de cookies GDPR
 * Maneja el consentimiento del usuario y la persistencia en localStorage
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

// Crear el contexto
const CookieContext = createContext();

/**
 * Hook personalizado para acceder al contexto de cookies
 * @returns {Object} Estado y funciones del contexto de cookies
 */
export const useCookieConsent = () => {
  const context = useContext(CookieContext);
  if (!context) {
    throw new Error('useCookieConsent debe usarse dentro de un CookieProvider');
  }
  return context;
};

/**
 * Provider del contexto de cookies
 * Gestiona el estado de consentimiento y lo persiste en localStorage
 */
export const CookieProvider = ({ children }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [consent, setConsent] = useState({
    necessary: true, // Siempre activadas
    functional: false,
    analytics: false,
    advertising: false,
  });

  /**
   * Cargar el consentimiento guardado desde localStorage al montar el componente
   */
  useEffect(() => {
    const savedConsent = localStorage.getItem('intercasa_cookie_consent');
    if (savedConsent) {
      try {
        const parsed = JSON.parse(savedConsent);
        setConsent(parsed);
        setShowBanner(false);
      } catch (error) {
        console.error('Error al parsear consentimiento guardado:', error);
        setShowBanner(true);
      }
    } else {
      // Si no hay consentimiento guardado, mostrar el banner
      setShowBanner(true);
    }
  }, []);

  /**
   * Acepta todas las cookies y guarda la preferencia
   */
  const acceptAll = () => {
    const allConsent = {
      necessary: true,
      functional: true,
      analytics: true,
      advertising: true,
    };
    setConsent(allConsent);
    localStorage.setItem('intercasa_cookie_consent', JSON.stringify(allConsent));
    setShowBanner(false);
    setShowConfig(false);
  };

  /**
   * Guarda las preferencias personalizadas del usuario
   * @param {Object} customConsent - Objeto con las preferencias seleccionadas
   */
  const savePreferences = (customConsent) => {
    const finalConsent = {
      ...customConsent,
      necessary: true, // Las cookies necesarias siempre están activas
    };
    setConsent(finalConsent);
    localStorage.setItem('intercasa_cookie_consent', JSON.stringify(finalConsent));
    setShowBanner(false);
    setShowConfig(false);
  };

  /**
   * Abre el panel de configuración de cookies
   */
  const openConfig = () => {
    setShowConfig(true);
  };

  /**
   * Cierra el panel de configuración
   */
  const closeConfig = () => {
    setShowConfig(false);
  };

  /**
   * Rechaza todas las cookies opcionales (solo mantiene las necesarias)
   */
  const rejectAll = () => {
    const minimalConsent = {
      necessary: true,
      functional: false,
      analytics: false,
      advertising: false,
    };
    setConsent(minimalConsent);
    localStorage.setItem('intercasa_cookie_consent', JSON.stringify(minimalConsent));
    setShowBanner(false);
    setShowConfig(false);
  };

  const value = {
    showBanner,
    showConfig,
    consent,
    acceptAll,
    rejectAll,
    openConfig,
    closeConfig,
    savePreferences,
  };

  return (
    <CookieContext.Provider value={value}>
      {children}
    </CookieContext.Provider>
  );
};

CookieProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
